import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/router';
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { bytesToSize } from '@formatting';
import { useBackgroundActivity } from '@hooks/useBackgroundActivity';
import { useBackgroundActivityRegistry } from '@hooks/useBackgroundActivityRegistry';
import { useMediaitemUploadService } from '@hooks/useMediaitemUploadService';
import { expandAllowedFileTypes, resolveContentType } from '@mime';
import { MediaitemUsageType } from '@models';
import {
  BackgroundActivityStatus,
  BackgroundJobActivityTarget,
  isUploadActivity
} from '@models/backgroundActivities';
import { randomString } from '@random';

export const useMediaitemUpload = ({
  target,
  allowedFileTypes,
  maxFileSize,
  mediaitemUsageType,
  onUploaded
}: {
  target: BackgroundJobActivityTarget | null;
  allowedFileTypes?: string[];
  maxFileSize?: number;
  mediaitemUsageType: MediaitemUsageType;
  onUploaded: (mediaitemId: number, originalFilename?: string, contentType?: string) => void;
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const registry = useBackgroundActivityRegistry();
  const uploadService = useMediaitemUploadService();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const onUploadedRef = useRef(onUploaded);
  useEffect(() => {
    onUploadedRef.current = onUploaded;
  });

  const inputId = useMemo(() => `file-upload-${randomString(36)}`, []);
  const acceptString = expandAllowedFileTypes(allowedFileTypes)?.join(',');

  const activity = useBackgroundActivity(target);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!activity || !isUploadActivity(activity)) return;
    if (activity.status !== BackgroundActivityStatus.COMPLETED || !activity.result) {
      return;
    }
    onUploadedRef.current(
      activity.result.mediaitemId,
      activity.result.originalFilename,
      activity.result.contentType
    );
    registry.markConsumed(activity.id);
  }, [activity?.id, activity?.status, activity?.result, registry]);

  useEffect(() => {
    setLocalError(null);
  }, [target]);

  const onChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (typeof maxFileSize === 'number' && file.size > maxFileSize) {
      setLocalError(t('components.fileInput.errorFileSize', { maxSize: bytesToSize(maxFileSize) }));
      event.target.value = '';
      return;
    }

    // UX-only: BE is the real gate, but drag-and-drop bypasses the `accept` filter.
    // Reject only on a confident mismatch so unresolvable types still reach the BE.
    if (
      mediaitemUsageType === MediaitemUsageType.IMPROVER_ATTACHMENT &&
      allowedFileTypes &&
      allowedFileTypes.length > 0
    ) {
      const resolvedType = resolveContentType(file.name, file.type);
      if (resolvedType && !allowedFileTypes.includes(resolvedType)) {
        setLocalError(t('components.fileInput.errorInvalidType'));
        event.target.value = '';
        return;
      }
    }

    setLocalError(null);

    if (!target) {
      // No target — silently no-op. Reachable only if a parent rendered a
      // disabled upload widget without a target; should never be triggered
      // by user interaction because the input is disabled in those paths.
      event.target.value = '';
      return;
    }

    if (activity && activity.status === BackgroundActivityStatus.FAILED) {
      registry.markConsumed(activity.id);
    }

    try {
      uploadService.startUpload({ target, file, mediaitemUsageType, returnUrl: router.asPath });
    } catch (err) {
      Sentry.captureException(err);
      setLocalError(t('components.fileInput.errorUpload'));
      event.target.value = '';
    }
  };

  const cancel = () => {
    if (activity?.id) {
      registry.cancel(activity.id);
    }
  };

  const uploadProgress = activity?.progress ?? null;
  const isUploading =
    activity?.status === BackgroundActivityStatus.PENDING ||
    activity?.status === BackgroundActivityStatus.RUNNING;
  const registryError =
    activity?.status === BackgroundActivityStatus.FAILED ? activity.error : null;
  const error = localError ?? registryError;

  return {
    inputId,
    fileInputRef,
    onChange,
    uploadProgress,
    error,
    acceptString,
    cancel,
    isUploading
  };
};
